import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { betSelection } from '../../shared/runners.ts'

/**
 * The bet slip: the selections a player has clicked, wherever they clicked them, until
 * they place or remove them. Kept in this browser so a reload does not empty it; the
 * prices on it are refreshed from the server on the Bet slip page, never trusted from here.
 */

export interface SlipSelection {
  marketId: string
  selectionId: number
  /** As shown when it was clicked, for the slip to read before fresh prices arrive. */
  event: string
  marketType: string
  runner: string
  price: number
}

interface Slip {
  selections: SlipSelection[]
  has: (marketId: string, selectionId: number) => boolean
  /** Add the selection, or take it off if it is already on: a price button toggles. */
  toggle: (s: SlipSelection) => void
  remove: (marketId: string, selectionId: number) => void
  /** Take the prices on offer now in place of the ones on the slip: accepting a price change. */
  reprice: (prices: { marketId: string; selectionId: number; price: number }[]) => void
  clear: () => void
}

/** A selection's names on the slip, as every bet list reads them — see `betSelection`. */
export function slipNames(runner: string, market: string): { runner: string; marketType: string } {
  const named = betSelection(runner, market)
  return { runner: named.label, marketType: named.market ?? '' }
}

const KEY = 'ks:betslip'
const MAX = 20

function load(): SlipSelection[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '[]')
    return Array.isArray(v) ? v.slice(0, MAX) : []
  } catch {
    return []
  }
}

const SlipContext = createContext<Slip | null>(null)

export function BetSlipProvider({ children }: { children: ReactNode }) {
  const [selections, setSelections] = useState<SlipSelection[]>(load)

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(selections))
    } catch {
      /* storage unavailable: the slip lasts as long as the tab */
    }
  }, [selections])

  const same = (a: { marketId: string; selectionId: number }, marketId: string, selectionId: number) =>
    a.marketId === marketId && a.selectionId === selectionId

  const has = useCallback(
    (marketId: string, selectionId: number) => selections.some((s) => same(s, marketId, selectionId)),
    [selections],
  )
  const toggle = useCallback((sel: SlipSelection) => {
    setSelections((prev) =>
      prev.some((s) => same(s, sel.marketId, sel.selectionId))
        ? prev.filter((s) => !same(s, sel.marketId, sel.selectionId))
        : [...prev, sel].slice(0, MAX),
    )
  }, [])
  const remove = useCallback((marketId: string, selectionId: number) => {
    setSelections((prev) => prev.filter((s) => !same(s, marketId, selectionId)))
  }, [])
  const reprice = useCallback((prices: { marketId: string; selectionId: number; price: number }[]) => {
    setSelections((prev) =>
      prev.map((s) => {
        const p = prices.find((x) => same(x, s.marketId, s.selectionId))
        return p ? { ...s, price: p.price } : s
      }),
    )
  }, [])
  const clear = useCallback(() => setSelections([]), [])

  const value = useMemo(
    () => ({ selections, has, toggle, remove, reprice, clear }),
    [selections, has, toggle, remove, reprice, clear],
  )
  return <SlipContext.Provider value={value}>{children}</SlipContext.Provider>
}

export function useBetSlip(): Slip {
  const slip = useContext(SlipContext)
  if (!slip) throw new Error('useBetSlip outside BetSlipProvider')
  return slip
}
